import React, {
  ChangeEvent,
  DragEvent,
  FC,
  useEffect,
  useRef,
  useState
} from 'react';
import { THUMBNAIL_SOURCE } from '../../config/constants';
import { useTranslate } from '@tolgee/react';
import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import toast from 'react-hot-toast';
import {
  addImageThumbnail,
  clearImageThumbnail,
  copyAnOverlay,
  createAnOverlay,
  saveOverlays,
  updateOverlayCreatorCloseState
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ALL,
  MAIN_COLOR_CODES,
  PRIMARY_DARK,
  UPLOAD_TYPE_IDEA_MEDIA,
  UPLOAD_TYPE_POST_MEDIA
} from '../../../../config/constants';
import { faCloudArrowUp } from '@fortawesome/pro-duotone-svg-icons';
import EditPreview from './editPreview';
import VideoFrameSelector from './selectThumbnail';
import { relocatePublishButtons } from '../../utils/uploadMediaFile';
import { Button } from 'react-bootstrap';
import SuccessToast from '../../cards/toast';
import { EditorHeader } from '../sharedComponents';
import Carousel from './Carousel';
import {
  generateFirebaseUploadUrl,
  isMediaImage
} from '../../../../redux/slices/lomavisCreator/utils';
import {
  compareAspectRatios,
  compressImage,
  determineImageDimensionsFromURL
} from '../../utils/helpers';
import axios from 'axios';
import axiosClient from '../../../../utils/axios';
import { lomavisShowToastr } from '../../../../utils/ui/toastr';

interface MediaUploadAreaProps {
  handleClose: Function;
  handleCloseNoConfirmation: Function;
}

const OverlayCreator: FC<MediaUploadAreaProps> = ({
  handleClose,
  handleCloseNoConfirmation
}) => {
  const { t } = useTranslate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [rightMargin, setRightMargin] = useState(0);
  const [selectedImageID, setSelectedImageID] = useState('');
  const [startSubmission, setStartSubmission] = useState(false);
  const dispatch = useDispatch();
  const footerRef = useRef(null);

  const platformEnabled = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.platformEnabled
  );

  const postData = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postData[ALL]
  );
  const allCreatedOverlays = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.createdOverlayArray
  );
  const location_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.locationUuid
  );
  const cloud_uuid = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.cloudUuid
  );

  const overlayUuids = new Set(
    allCreatedOverlays?.map((item) => item.imageUuid)
  );

  useEffect(() => {
    setSelectedImageID(postData?.media[0]?.uuid);
    relocatePublishButtons({
      footerRef: footerRef,
      setRightMargin: setRightMargin
    });
  }, []);

  const [dragIsOver, setDragIsOver] = useState(false);

  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Define the event handlers
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    setDragIsOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragIsOver(false);
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragIsOver(false);

    const files = Array.from(event.dataTransfer.files);

    const allowedTypes = ['image/svg+xml'];
    const filteredFiles = files.filter((file) =>
      allowedTypes.includes(file?.type)
    );

    if (filteredFiles.length > 0) {
      await compareAndUploadFile(filteredFiles[0]);

      filteredFiles.map((item) => {
        items.includes(item) ? '' : setItems([...items, item]);
      });
    } else {
      lomavisShowToastr(t('errors.lomavisCreator.upload_svg_file'), 'error');
    }
  };

  const handleUploadClick = async () => {
    // 👇 We redirect the click event onto the hidden input element
    inputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    const fileList = e.target.files; // This is a FileList
    const fileArray = Array.from(fileList); // Convert the FileList to an array

    console.log(
      'file',
      postData?.media.find((item) => item.uuid === selectedImageID)
        ?.original_asset
    );

    await compareAndUploadFile(fileArray[0]);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const compareAndUploadFile = async (file) => {
    await compareAspectRatios(
      file,
      postData?.media.find((item) => item.uuid === selectedImageID)
        ?.original_asset
    )
      .then((isMatching) => {
        isMatching
          ? handleUpload(file)
          : lomavisShowToastr(
              t('errors.lomavisCreator.aspect_ratio_dont_match'),
              'error'
            );
      })
      .catch(() =>
        lomavisShowToastr(
          t('errors.lomavisCreator.aspect_ratio_comparison'),
          'error'
        )
      );
  };

  const generateTransparentSVGAndUpload = async () => {
    const selectedImage = postData?.media.find(
      (item) => item.uuid === selectedImageID
    )?.original_asset;

    if (!selectedImage) {
      toast.error('No image selected.', {
        duration: 4000,
        position: 'top-right'
      });
      return;
    }

    const selectedImageDimensions = await determineImageDimensionsFromURL(
      selectedImage
    );

    if (!selectedImageDimensions) {
      toast.error('Failed to determine image dimensions.', {
        duration: 4000,
        position: 'top-right'
      });
      return;
    }

    const { width, height } = selectedImageDimensions;

    // Create an SVG with transparent background
    const svgContent = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"></svg>`;
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });

    // Create a File object
    const transparentSVG = new File([blob], 'transparent_overlay.svg', {
      type: 'image/svg+xml'
    });

    // Upload like a normal SVG
    handleUpload(transparentSVG);
  };

  const handleUpload = async (filteredFile: any) => {
    if (isMediaImage(filteredFile)) {
      setUploading(true);

      const file = await compressImage(filteredFile);

      let preview_image = URL.createObjectURL(file);
      const file_type = file.type;
      const fileSizeInBytes = file.size;

      let dimensions = await determineImageDimensionsFromURL(
        preview_image
      ).catch((error) => {
        console.error('Error getting image dimensions:', error);
        throw error;
      });
      let uploadPreset = await generateFirebaseUploadUrl(
        {
          content_type: file_type,
          filesize_in_bytes: fileSizeInBytes,
          filename: file.name,
          location_uuid: location_uuid,
          cloud_uuid: cloud_uuid,
          width: dimensions?.width,
          height: dimensions?.height,
          upload_type: UPLOAD_TYPE_IDEA_MEDIA
        },
        {},
        () => console.log('error here')
      );

      const url = uploadPreset?.url;
      const headers = uploadPreset?.headers;

      try {
        const config = {
          headers: headers ? { ...headers, 'Content-Type': file_type } : {} // Set headers property with provided headers
        };

        const response = await axios.put(url, file, {
          ...config
        });

        console.log('return data', response);

        try {
          const res = await axiosClient.put(uploadPreset?.confirm_url, {
            success: true
          });

          await dispatch(
            createAnOverlay({
              overlayImage: uploadPreset?.uploaded_object_data.original_asset,
              overlayUuid: uploadPreset?.uuid,
              imageUuid: selectedImageID
            })
          );
          setUploading(false);
          return res.data;
        } catch (err: any) {
          console.log(err);
          throw err;
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Request canceled:', error.message);
        } else {
          toast.error(`${t('errors.request_couldnt_be_completed')}`, {
            duration: 4000,
            position: 'top-right'
          });
          const res = error.response;

          console.error(
            `Looks like there was a problem. Status Code: ${error}`
          );

          console.error('Error:', error);
          throw error;
        }
        toast.error(` ${t('errors.request_couldnt_be_completed')}`, {
          duration: 4000,
          position: 'top-right'
        });

        // Dispatch deleteAfile action inside the catch block

        setUploading(false);
      }
    }

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };
  const cancelEdits = () => {
    // dispatch(
    //   clearImageThumbnail({
    //     platform,
    //   })
    // );
    handleClose();
  };

  const [showToast, setShowToast] = useState(false);

  const triggerSuccessToast = () => {
    toast.success(t('lomavisCreator.template.saved'), {
      position: 'top-right',
      id: 'overlaysuccess'
    });
  };

  const handleSubmit = () => {
    setStartSubmission(true);
    dispatch(saveOverlays());
    dispatch(
      updateOverlayCreatorCloseState({
        state: false
      })
    );

    triggerSuccessToast();

    setTimeout(() => {
      setStartSubmission(false);
    }, 500);
  };
  return (
    <div className="w-100 h-100 ">
      <SuccessToast show={showToast} onClose={() => setShowToast(false)} />
      <div className="d-flex flex-column w-100 h-100 gap-4 justify-content-between">
        <EditorHeader>
          <div
            style={{}}
            className="d-flex flex-fill align-items-center h-100 main_editor"
          >
            <h2>{t('lomavisCreator.overlayCreator.header')}</h2>
          </div>
        </EditorHeader>

        <div className="d-flex w-100 flex-fill gap-4">
          <div
            style={{ overflowY: 'auto', width: '120px' }}
            className="bg-white p-2 h-100 rounded-3 mx-auto"
          >
            {' '}
            <Carousel
              items={postData?.media}
              selectedImageID={selectedImageID}
              setSelectedImageID={setSelectedImageID}
            />
          </div>

          <div className="w-100 flex-fill">
            <div
              style={{
                height: '100%',
                width: '100%',
                position: 'relative'
              }}
              onDragOver={handleDragOver}
              className="d-flex flex-column w-100 h-100 position-relative"
            >
              {
                <div className="w-100 h-100 ">
                  {allCreatedOverlays?.find(
                    (item) => item?.imageUuid === selectedImageID
                  ) ? (
                    <div className="w-100 h-100">
                      <EditPreview
                        handleClose={handleClose}
                        handleCloseNoConfirmation={handleCloseNoConfirmation}
                        currentOverlay={allCreatedOverlays?.find(
                          (item) => item?.imageUuid === selectedImageID
                        )}
                        bgImg={
                          postData?.media.find(
                            (item) => item.uuid === selectedImageID
                          )?.original_asset
                        }
                        selectedImage={selectedImageID}
                        successNotification={() => triggerSuccessToast()}
                        inputRef={inputRef}
                      />
                    </div>
                  ) : (
                    <div className="bg-white rounded-2 p-6 w-100 h-100 ">
                      <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className="w-100 h-100 d-flex flex-column justify-content-center justify-content-center p-6 rounded-2 border-secondary gap-2"
                        style={{
                          border: '2px dashed',

                          backgroundColor: dragIsOver && 'rgba(0, 0, 0, 0.02)'
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '150px',
                            textAlign: 'center',
                            position: 'relative'
                          }}
                          className="text-dark d-flex justify-content-center flex-column gap-2"
                        >
                          {uploading ? (
                            <div
                              className="spinner-border mx-auto d-flex justify-content-center "
                              role="status"
                              style={{
                                borderWidth: '5px',
                                borderRadius: '50%',
                                height: '40px',
                                width: '40px',
                                color: MAIN_COLOR_CODES[PRIMARY_DARK]
                              }}
                            ></div>
                          ) : (
                            <i
                              className="fa-solid fa-cloud-arrow-up"
                              style={{
                                color: MAIN_COLOR_CODES[PRIMARY_DARK],
                                fontSize: '9rem'
                              }}
                            ></i>
                          )}
                          <p
                            style={{ color: MAIN_COLOR_CODES[PRIMARY_DARK] }}
                            className="fs-1 fw-bold"
                          >
                            {t('creator.text.drop_file_here')}
                          </p>
                        </div>
                        <div
                          style={{ color: MAIN_COLOR_CODES[PRIMARY_DARK] }}
                          className="fs-3 text-center"
                        >
                          {t('or')}
                        </div>
                        <div className="d-flex gap-4 align-items-center justify-content-center">
                          <Button
                            variant="secondary"
                            size="sm"
                            style={{ color: MAIN_COLOR_CODES[PRIMARY_DARK] }}
                            className="fs-5 text-center cursor-pointer"
                            onClick={() => handleUploadClick()}
                          >
                            {' '}
                            {t('lomavisCreator.thumbnail.uploadFromDevice')}
                          </Button>

                          <Button
                            variant="secondary"
                            size="sm"
                            style={{ color: MAIN_COLOR_CODES[PRIMARY_DARK] }}
                            className="fs-5 text-center cursor-pointer"
                            onClick={() => {
                              generateTransparentSVGAndUpload();
                            }}
                          >
                            {' '}
                            {t('lomavisCreator.thumbnail.contWithout')}
                          </Button>
                        </div>

                        {allCreatedOverlays?.length > 0 && (
                          <div className="w-100 d-flex flex-column justify-content-center">
                            <h4 className="w-100 d-flex justify-content-center">
                              {t('lomavisCreator.overlay.select_from')}
                            </h4>
                            <div className="w-100 d-flex gap-2 justify-content-center">
                              {postData?.media.map((item, index) => {
                                if (overlayUuids.has(item?.uuid)) {
                                  return (
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dispatch(
                                          copyAnOverlay({
                                            newUuid: selectedImageID,
                                            oldUuid: item?.uuid
                                          })
                                        );
                                      }}
                                    >
                                      {t('lomavisCreator.overlay.overlay')}{' '}
                                      {index + 1}
                                    </Button>
                                  );
                                }
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              }
            </div>
          </div>
          <input
            type="file"
            ref={inputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            multiple={true}
            accept=".svg"
            max={1}
          />
        </div>

        <div
          ref={footerRef}
          style={{ bottom: '0', zIndex: 100, height: '60px' }}
          className="w-100 d-flex justify-content-end gap-3 rounded-3 align-items-center bg-white py-2 px-3 border-top h-60px bg-white"
        >
          <Button onClick={cancelEdits} variant="secondary" size="sm">
            {t('lomavisCreator.cancel')}
          </Button>

          <Button
            style={{ marginRight: rightMargin + 'px' }}
            variant="primary"
            size="sm"
            onClick={() => {
              handleSubmit();
            }}
            disabled={
              !allCreatedOverlays?.find(
                (item) => item?.imageUuid === selectedImageID
              )
            }
          >
            {startSubmission && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
            )}
            {startSubmission
              ? t('lomavisCreator.buttons.saving')
              : t('lomavisCreator.buttons.save')}
          </Button>
        </div>
      </div>
      {/* {!thumbnailImage &&
        thumbnailStates.source === THUMBNAIL_SOURCE.UPLOAD_FILE && (
          <div
            style={{
              height: '100%',
              width: '100%',
              position: 'relative'
            }}
            onDragOver={handleDragOver}
            className="d-flex flex-column w-100 h-100 position-relative"
          >
            {
              <div className="bg-white rounded-2 p-6 w-100 h-100 ">
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => handleUploadClick()}
                  className="w-100 h-100 d-flex flex-column justify-content-center justify-content-center p-6 rounded-2 border-secondary"
                  style={{
                    border: '2px dashed',

                    backgroundColor: dragIsOver && 'rgba(0, 0, 0, 0.02)'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '200px',
                      textAlign: 'center',
                      position: 'relative'
                    }}
                    className="text-dark d-flex flex-column gap-3"
                  >
                    <i
                      className="fa-solid fa-cloud-arrow-up"
                      style={{
                        color: MAIN_COLOR_CODES[PRIMARY_DARK],
                        fontSize: '9rem'
                      }}
                    ></i>
                    <p
                      style={{ color: MAIN_COLOR_CODES[PRIMARY_DARK] }}
                      className="fs-1 fw-bold"
                    >
                      {t('creator.text.drop_file_here')}
                    </p>
                  </div>
                  <div
                    style={{ color: MAIN_COLOR_CODES[PRIMARY_DARK] }}
                    className="fs-3 text-center"
                  >
                    {t('or')}
                  </div>
                  <div
                    style={{ color: MAIN_COLOR_CODES[PRIMARY_DARK] }}
                    className="fs-3 fw-bold text-center"
                  >
                    {' '}
                    {t('lomavisCreator.thumbnail.uploadFromDevice')}
                  </div>
                  <input
                    type="file"
                    ref={inputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    multiple={true}
                    accept=".jpeg, .jpg, .png, .mp4"
                    max={2}
                  />
                </div>
              </div>
            }

            <div
              ref={footerRef}
              style={{ bottom: '0', zIndex: 100, height: '60px' }}
              className="w-100 d-flex justify-content-end gap-3 align-items-center bg-white py-2 border-top rounded-3 "
            >
              <Button
                style={{ marginRight: rightMargin + 'px' }}
                onClick={cancelEdits}
                variant="secondary"
                size="sm"
              >
                {t('lomavisCreator.cancel')}
              </Button>
            </div>
          </div>
        )}

      {thumbnailImage && (
        <div className="w-100 h-100">
          <EditPreview
            handleClose={handleClose}
            handleCloseNoConfirmation={handleCloseNoConfirmation}
            platform={platform}
            videoUrl={
              source_media_uuid ? videoUrl : videoBlob ? videoBlob : videoUrl
            }
            successNotification={() => triggerSuccessToast()}
          />
        </div>
      )} */}
    </div>
  );
};

export default OverlayCreator;
